import { Box, Divider, InputAdornment, Typography } from '@mui/material'
import { IconSearch } from 'assets/icons'
import { Button, TextField } from 'elements/inputs'
import { Menu } from 'elements/navigation'
import { useSearch } from 'lib/hooks'
import { SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppLanguages } from './use-app-languages'
import { useLanguagesList } from './use-languages-list'

export const ChangeLanguageMenu = () => {
  const { t, i18n } = useTranslation()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const open = Boolean(anchorEl)

  const id = open ? 'languageChangePopover' : undefined

  const { value, search, onChange } = useSearch()

  const { systemLanguages } = useAppLanguages()
  const { allLanguages } = useLanguagesList()

  if (!systemLanguages || !allLanguages) {
    return null
  }

  const languages = allLanguages
    .filter(lang => systemLanguages.some(item => item.code === lang.code))
    .filter(language =>
      language.name.toLowerCase().includes((search ?? '').toLowerCase()),
    )
    .slice(0, 10)

  return (
    <>
      <Button
        aria-label="open-menu-button"
        sx={{
          color: 'text.primary',
          mr: 2,
          height: 25,
        }}
        variant="outlined"
        onClick={(event: {
          currentTarget: SetStateAction<HTMLButtonElement | null>
        }) => systemLanguages.length > 1 && setAnchorEl(event.currentTarget)}
      >
        {
          allLanguages.find(
            lang =>
              lang.code ===
              (i18n.language.includes('en') ? 'en' : i18n.language),
          )?.name
        }
      </Button>

      <Menu
        aria-label="language-menu"
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={() => setAnchorEl(null)}
        width="236px"
        sx={{
          mt: 1,
        }}
      >
        <Box px={1}>
          {systemLanguages.length >= 10 && (
            <>
              <Box p={1} aria-label="search-input">
                <TextField
                  autoFocus
                  startAdornment={
                    <InputAdornment position="start">
                      <IconSearch sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  }
                  value={value}
                  onChange={onChange}
                  placeholder={t('common.start_typing')}
                  sx={{ border: 0, pl: 0, bgcolor: 'transparent', height: 23 }}
                />
              </Box>
              <Divider sx={{ height: 0.5 }} />
            </>
          )}
          <Box
            aria-label="languages-list-box"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pb: 1,
              maxHeight: 330,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'text.secondary',
                borderRadius: '4px',
              },
            }}
          >
            {languages.length > 0 ? (
              languages.map(lang => (
                <Typography
                  key={lang.code}
                  variant="caption2"
                  color="text.primary"
                  sx={{
                    p: 1,
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      transition: '0.3s',
                    },
                  }}
                  onClick={() => {
                    onChange('')
                    setAnchorEl(null)
                    i18n.changeLanguage(lang.code)
                  }}
                >
                  {lang.name}
                </Typography>
              ))
            ) : (
              <Typography variant="caption3" textAlign="center">
                {t('common.languages_not_found')}
              </Typography>
            )}
          </Box>
        </Box>
      </Menu>
    </>
  )
}
